<script setup lang="ts">
// Pick people to message or add: everyone you share a space or conversation with.
import { computed, ref } from 'vue'
import { useSession } from '../stores/session'
import Avatar from './Avatar.vue'

const props = withDefaults(
  defineProps<{
    /** People not to offer (already in the conversation). */
    exclude?: string[]
    /** Most people that may be picked. */
    max: number
    submitLabel: string
    busy?: boolean
    /** What the picked people would make, e.g. "Group of 4". */
    summary?: (picked: number) => string
  }>(),
  { exclude: () => [], busy: false, summary: undefined },
)
const emit = defineEmits<{ pick: [users: string[]] }>()
const session = useSession()

const contacts = computed(() =>
  [...session.profiles.values()].filter((p) => p.user_id !== session.me && !props.exclude.includes(p.user_id)),
)
const picked = ref<string[]>([])
const query = ref('')
const shown = computed(() => {
  const q = query.value.trim().toLowerCase()
  return q ? contacts.value.filter((p) => `${p.display_name} ${p.user_id}`.toLowerCase().includes(q)) : contacts.value
})
function toggle(id: string): void {
  if (picked.value.includes(id)) picked.value = picked.value.filter((p) => p !== id)
  else if (picked.value.length < props.max) picked.value = [...picked.value, id]
}
</script>

<template>
  <div>
    <p v-if="contacts.length === 0" class="text-sm text-text-muted">No one else to add: people show up here once you share a space.</p>
    <input v-if="contacts.length > 8" v-model="query" class="input mb-2" placeholder="Search people" aria-label="Search people" />
    <label
      v-for="p in shown"
      :key="p.user_id"
      class="flex w-full cursor-pointer items-center gap-3 border-b border-border px-1 py-2.5 text-left last:border-0 hover:bg-bg-hover"
      :data-testid="`contact-${p.display_name}`"
    >
      <input
        type="checkbox"
        class="shrink-0"
        :checked="picked.includes(p.user_id)"
        :disabled="!picked.includes(p.user_id) && picked.length >= max"
        @change="toggle(p.user_id)"
      />
      <Avatar :id="p.user_id" :name="p.display_name" :size="28" />
      <div class="min-w-0">
        <div class="truncate text-sm">{{ p.display_name }}</div>
        <div class="mono truncate text-text-muted">{{ p.user_id }}</div>
      </div>
    </label>
    <div v-if="contacts.length" class="mt-4 flex items-center justify-between gap-2">
      <span class="mono text-text-muted">{{ summary?.(picked.length) ?? '' }}</span>
      <button class="btn-primary" :disabled="picked.length === 0 || busy" data-testid="start-dm" @click="emit('pick', picked)">
        {{ busy ? 'Working…' : submitLabel }}
      </button>
    </div>
  </div>
</template>
